import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import { usePopoverDismissOutside } from './usePopoverDismissOutside';
import { BACKGROUND_ANALYSIS_CAP_HINT } from './portalBackgroundAnalysisChat';
import {
  backgroundAnalysisCoverageHint,
  backgroundAnalysisProgressHint,
  type AnalysisCoverage,
  type AnalysisProgress,
} from './portalBackgroundAnalysisChunks';
import { shortDocumentLabel } from './portalBackgroundJobs';

/**
 * Klasse ANALYSE_POPOVER_READ_SIZE — Anteil der ARKO-Chatfläche (`.portal-arko-pane`).
 * Polar: VERBOTEN Mini-Dropdown-Scroll-Käfig; Modus/Werkzeuge auf ¾ aufblasen.
 *        ERLAUBT nur Hintergrundanalyse-Popover ~75 % Breite+Höhe, lesbarer Fließtext.
 */
export const ANALYSE_POPOVER_CHAT_COVERAGE = 0.75;

export type BackgroundAnalysisJobView = {
  request_id: string;
  documentName: string;
  status: 'running' | 'done' | 'error' | 'cancelled';
  workerText: string | null;
  coverage?: AnalysisCoverage | null;
  progress?: AnalysisProgress | null;
};

type ArkoBackgroundAnalysisChipProps = {
  job: BackgroundAnalysisJobView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: (requestId: string) => void;
  onDismiss: (requestId: string) => void;
};

function statusLabel(job: BackgroundAnalysisJobView): string {
  if (job.status === 'running' && job.progress) {
    if (job.progress.phase === 'synthesis') return 'Synthese';
    return `Abschnitt ${job.progress.chunkIndex}/${job.progress.chunkTotal}`;
  }
  if (job.status === 'running') return 'läuft';
  if (job.status === 'done') return 'fertig';
  if (job.status === 'cancelled') return 'abgebrochen';
  return 'Fehler';
}

function hintForJob(job: BackgroundAnalysisJobView): string {
  if (job.coverage) return backgroundAnalysisCoverageHint(job.coverage);
  if (job.progress) return backgroundAnalysisProgressHint(job.progress);
  return BACKGROUND_ANALYSIS_CAP_HINT;
}

function resolveArkoPane(chip: HTMLElement | null): HTMLElement | null {
  const pane = chip?.closest('.portal-arko-pane');
  return pane instanceof HTMLElement ? pane : null;
}

/**
 * Klasse ARKO_ANALYSE_FRESH_WINDOW + DOKUMENTKONTEXT_JOB_COVERS_EXTRACT
 * Polar: Outside-Click + Escape schließen (POPOVER_DISMISS_OUTSIDE).
 * Chip-Aktion (Abbrechen) feuern wir nicht beim Outside-Click.
 * Klasse ANALYSE_POPOVER_READ_SIZE: Overlay ~75 % der ARKO-Chatfläche, nicht Mini-Dropdown.
 * Klasse ANALYSE_POPOVER_PANE_CENTER: Overlay in `.portal-arko-pane` zentriert
 * (createPortal + absolute inset-0 + Flex-Center). VERBOTEN Chip als Anker /
 * Viewport der ganzen Portal-Seite / Mini-22rem.
 */
export const ArkoBackgroundAnalysisChip: React.FC<ArkoBackgroundAnalysisChipProps> = ({
  job,
  open,
  onOpenChange,
  onCancel,
  onDismiss,
}) => {
  const chipRef = useRef<HTMLSpanElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [paneEl, setPaneEl] = useState<HTMLElement | null>(null);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  usePopoverDismissOutside({
    open: Boolean(open && paneEl),
    onClose: close,
    rootRef: overlayRef,
    ignoreCloseRef: chipRef,
    closeOnScroll: false,
  });

  useLayoutEffect(() => {
    if (!open) {
      setPaneEl(null);
      return undefined;
    }
    const pane = resolveArkoPane(chipRef.current);
    setPaneEl(pane);
    if (!pane || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const next = resolveArkoPane(chipRef.current);
      setPaneEl((prev) => (prev === next ? prev : next));
    });
    ro.observe(pane);
    return () => ro.disconnect();
  }, [open]);

  const running = job.status === 'running';
  const label = running
    ? `Analysiere ${shortDocumentLabel(job.documentName)}…`
    : `Analyse ${shortDocumentLabel(job.documentName)}`;

  const coveragePct = `${ANALYSE_POPOVER_CHAT_COVERAGE * 100}%`;
  const overlay =
    open && paneEl
      ? createPortal(
          <div
            className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center"
            data-analyse-popover-pane-center="1"
            data-testid="portal-background-analysis-popover-layer"
          >
            <div
              ref={overlayRef}
              role="dialog"
              aria-label={`Hintergrundanalyse ${job.documentName}`}
              data-testid="portal-background-analysis-popover"
              data-analyse-popover-read-size={String(ANALYSE_POPOVER_CHAT_COVERAGE)}
              data-analyse-popover-pane-center="1"
              className="pointer-events-auto flex flex-col rounded-xl border border-white/[0.1] bg-[#141619] shadow-[0_16px_48px_rgba(0,0,0,0.55)] overflow-hidden"
              style={{ width: coveragePct, height: coveragePct }}
            >
              <div className="flex-shrink-0 flex items-start justify-between gap-3 px-4 pt-3 pb-2">
                <p className="m-0 text-[13px] font-semibold text-violet-100">
                  Analyse · {shortDocumentLabel(job.documentName, 56)} · {statusLabel(job)}
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="rounded-full p-1 text-slate-400 hover:bg-white/[0.06] hover:text-slate-100"
                  aria-label="Analyse-Popover schließen"
                  title="Schließen (Escape)"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <p
                className="flex-shrink-0 m-0 px-4 pb-2 text-[12px] leading-snug text-amber-100/90"
                data-testid="portal-background-analysis-progress"
              >
                {hintForJob(job)}
              </p>
              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar mx-4 mb-4 rounded-md border border-white/[0.06] bg-slate-950/60 px-4 py-3 text-[13px] leading-[1.55] text-slate-100 whitespace-pre-wrap">
                {running
                  ? (job.workerText?.trim()
                    || job.progress?.notes
                    || 'Analyse läuft abschnittsweise (frisches Fenster, ohne Chatverlauf). Notizen erscheinen hier nach jedem Abschnitt.')
                  : job.workerText?.trim() || 'Kein Auswertungstext.'}
              </div>
            </div>
          </div>,
          paneEl,
        )
      : null;

  return (
    <span ref={chipRef} className="inline-flex">
      <span className="inline-flex items-center gap-0.5 rounded-full border border-violet-400/25 bg-violet-500/10 pl-1.5 pr-0.5 py-0">
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className="inline-flex items-center"
          aria-expanded={open}
          aria-haspopup="dialog"
          data-testid="portal-chip-background-analysis"
          title="Analyse-Popover öffnen"
        >
          <PortalBadge
            label={label}
            accent="violet"
            icon={
              running ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : job.status === 'done' ? (
                <CheckCircle2 className="h-3 w-3" aria-hidden />
              ) : (
                <AlertCircle className="h-3 w-3" aria-hidden />
              )
            }
            className="!border-0 !bg-transparent !px-0 !py-0 !text-[9px] !font-medium"
          />
        </button>
        <button
          type="button"
          onClick={() => {
            if (running) onCancel(job.request_id);
            else onDismiss(job.request_id);
          }}
          className="rounded-full p-0.5 text-violet-200/80 hover:bg-violet-500/20 hover:text-violet-100"
          aria-label={
            running
              ? `Analyse „${job.documentName}“ abbrechen`
              : `Analyse „${job.documentName}“ ausblenden`
          }
          title={running ? 'Abbrechen' : 'Ausblenden'}
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      </span>
      {overlay}
    </span>
  );
};
